import React, { useCallback, useState } from 'react';
import moment from 'moment';
import { TabsOption } from 'global';
import { formatCurrency } from 'utils';
import { usePdf } from 'hooks/usePdf';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { REACT_APP_OCA_BASE_URL } from 'config';
import { accountSelectors } from 'redux/accountReducer';
import { DateFormats, NO_DATA } from 'constants/common';
import { useParams, useHistory } from 'react-router-dom';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { ConditionBadge, EquipmentImagesCarousel } from 'modules/equipment';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { GET_EQUIPMENT_DETAILS_BY_ID } from 'modules/equipment/api/equipment';
import { Edit, Label, PictureAsPdf, QrCode2, Send, Visibility } from '@mui/icons-material';
import { SendEquipmentFlyerModal } from 'modules/equipment/ui/components/SendEquipmentFlyerModal';

import { MediaTab } from './MediaTab';
import { AttachmentsTab } from './AttachmentsTab';
import { EquipmentInfoTab } from './EquipmentInfoTab';
import { EquipmentDetailsTabbedContent } from './EquipmentDetailsTabbedContent';
import { EquipmentDetailsTabs, EquipmentDetailsResponse, EquipmentDetailsVariables } from './EquipmentDetailsTypes';

export const EquipmentDetailsPage = () => {
  const history = useHistory();
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const account = useSelector(accountSelectors.account);

  const [isSendFlyerModalOpen, setIsSendFlyerModalOpen] = useState(false);
  const [forceExpanded, setForceExpanded] = useState(false);

  const { contentRef, handleDownloadPdf: triggerPdf } = usePdf();

  const handleDownloadPdf = useCallback(() => {
    setForceExpanded(true);

    requestAnimationFrame(() => {
      triggerPdf();
      setForceExpanded(false);
    });
  }, [triggerPdf]);

  const { data, loading, error } = useQuery<EquipmentDetailsResponse, EquipmentDetailsVariables>(
    GET_EQUIPMENT_DETAILS_BY_ID,
    {
      variables: {
        id: equipmentId,
      },
      skip: !equipmentId,
    }
  );

  const equipment = data?.equipment;
  const assets = equipment?.assets ?? [];
  const specs = equipment?.specs ?? null;
  const attachments = equipment?.attachments ?? [];
  const equipmentName = equipment ? [equipment.year, equipment.make, equipment.model].filter(Boolean).join(' ') : '';

  const handleViewFlyer = () => {
    const vendorGUID = account?.dynamicsAccountId;

    if (!vendorGUID || !equipmentId) return;

    const url = new URL(`${REACT_APP_OCA_BASE_URL}/psequipmentflyer`);

    url.searchParams.set('vendorGUID', vendorGUID);
    url.searchParams.append('equipmentIds', equipmentId);

    window.open(url.toString(), '_blank');
  };

  const breadcrumbConfig = [
    {
      label: 'Inventory Management',
      value: 'inventory' as const,
      onClick: () => history.push('/inventory'),
    },
    {
      label: 'Equipment Details',
      value: 'equipmentDetails' as const,
      onClick: () => {},
    },
  ];

  const tabs: TabsOption<EquipmentDetailsTabs>[] = [
    {
      label: 'Equipment Info',
      value: EquipmentDetailsTabs.EquipmentInfo,
      isVisible: true,
      component: <EquipmentInfoTab equipment={equipment} specs={specs} />,
    },
    {
      label: 'Attachments',
      value: EquipmentDetailsTabs.Attachments,
      isVisible: true,
      component: <AttachmentsTab attachments={attachments} equipment={equipment} />,
    },
    {
      label: 'Media',
      value: EquipmentDetailsTabs.Media,
      isVisible: true,
      component: <MediaTab assets={assets} />,
    },
  ];

  return (
    <PageContainer title="Equipment Details" loading={loading} error={error}>
      <Stack
        sx={{
          gap: '0.5rem',
          flexDirection: 'column',
          padding: {
            xs: '0 1rem 1rem',
            md: '0 1.5rem 1.5rem',
          },
        }}
      >
        <Paper
          elevation={1}
          sx={{
            padding: {
              xs: '1rem',
              md: '1.5rem',
            },
            borderRadius: '0.5rem',
          }}
        >
          <Breadcrumbs currentValue="equipmentDetails" config={breadcrumbConfig} />

          <Stack
            justifyContent="space-between"
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Typography variant="h5" fontWeight={600}>
              Equipment Details
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} gap={1}>
              <Typography color="textSecondary">
                Last Updated:{' '}
                {equipment?.modifiedDateTime
                  ? moment(equipment.modifiedDateTime).format(DateFormats.DateTimeMedium24Hour)
                  : NO_DATA}
              </Typography>

              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => history.push(`/equipment/${equipmentId}`, { from: 'equipmentDetails' })}
              >
                Edit
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper ref={contentRef} elevation={1} sx={{ padding: { xs: '1rem', md: '1.5rem' }, borderRadius: '0.5rem' }}>
          <Stack
            mb={2}
            gap={2}
            justifyContent="space-between"
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
          >
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {equipmentName || NO_DATA}
              </Typography>

              <Stack direction="row" gap={2} alignItems="center" mt={0.5} flexWrap="wrap">
                {equipment?.serialNumberVin && (
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <QrCode2 fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="textSecondary">
                      VIN: {equipment.serialNumberVin}
                    </Typography>
                  </Stack>
                )}

                {equipment?.stockId && (
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <Label fontSize="small" sx={{ color: 'text.secondary' }} />

                    <Typography variant="body2" color="textSecondary">
                      Stock #: {equipment.stockId}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>

            <Stack
              gap="1rem"
              direction="row"
              flexWrap="wrap"
              data-pdf-exclude
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button variant="outlined" size="small" startIcon={<PictureAsPdf />} onClick={handleDownloadPdf}>
                Download PDF
              </Button>

              <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={handleViewFlyer}>
                View Flyer
              </Button>

              <Button
                size="small"
                variant="contained"
                startIcon={<Send />}
                onClick={() => setIsSendFlyerModalOpen(true)}
              >
                Send Flyer
              </Button>
            </Stack>
          </Stack>

          <Stack
            gap="2rem"
            direction={{
              lg: 'row',
              xl: 'row',
              xs: 'column',
              sm: 'column',
              md: 'column',
            }}
          >
            <Stack gap="0.5rem">
              <EquipmentImagesCarousel assets={assets} />

              <Stack direction="row" gap={1} mt={1.5} alignItems="center" flexWrap="wrap">
                {equipment?.inventoryItem?.status && (
                  <Chip label={equipment.inventoryItem.status} size="small" variant="outlined" color="success" />
                )}

                {(equipment?.inventoryItem?.condition || equipment?.newUsed) && (
                  <>
                    <Typography variant="body2" color="textSecondary">
                      •
                    </Typography>

                    <ConditionBadge condition={(equipment?.inventoryItem?.condition || equipment?.newUsed)!} />
                  </>
                )}
              </Stack>

              <Stack>
                <Typography variant="caption" color="textSecondary">
                  List Price:
                </Typography>

                <Typography variant="h6" fontWeight={600}>
                  {equipment?.retailPrice != null ? `${formatCurrency(equipment.retailPrice, true)} USD` : NO_DATA}
                </Typography>
              </Stack>
            </Stack>

            <Stack width="100%" gap="0.5rem">
              <Stack>
                <Typography variant="subtitle1" color="textSecondary" sx={{ textTransform: 'none' }}>
                  Description
                </Typography>

                <Typography variant="body1">{equipment?.additionalDescription || NO_DATA}</Typography>

                <Typography color="textSecondary" sx={{ display: 'block', textAlign: 'right' }}>
                  Created:{' '}
                  {equipment?.createdDateTime
                    ? moment(equipment.createdDateTime).format(DateFormats.DateTimeMedium24Hour)
                    : NO_DATA}
                </Typography>
              </Stack>

              <EquipmentDetailsTabbedContent<EquipmentDetailsTabs> tabs={tabs} forceExpanded={forceExpanded} />
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      {equipmentId && (
        <SendEquipmentFlyerModal
          equipmentId={equipmentId}
          open={isSendFlyerModalOpen}
          onClose={() => setIsSendFlyerModalOpen(false)}
        />
      )}
    </PageContainer>
  );
};
